import { FC, useState } from 'react';
import { Link } from 'react-router-dom';

import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import {
  Badge,
  CardMedia,
  Link as MUILink,
  Popover,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';

import { AppPath } from '@/shared/constants/path';
import ProductVariant from '@/shared/types/products/productVariant.type';
import formatCurrency from '@/shared/utils/formatCurrency';

import ProductItemDetailsPopover from './ProductItemDetailsPopover';

type ProductItemProps = {
  currency: string;
  product: ProductVariant;
  onRemoveSelectedProduct: (id: string) => void;
  onUpdateSelectedProducts: (product: ProductVariant) => void;
};

const ProductItem: FC<ProductItemProps> = ({
  currency,
  product,
  onRemoveSelectedProduct,
  onUpdateSelectedProducts,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const onOpenDetails = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onCloseDetails = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  const {
    product_id,
    variant_id,
    product_name,
    variant_name,
    product_qty,
    sku,
    product_prices,
    value,
  } = product;

  const getName = () => {
    if (product_name && variant_name) {
      if (variant_name === '_default') {
        return product_name;
      }
      return `${product_name}: ${variant_name}`;
    } else if (product_name && !variant_name) {
      return product_name;
    } else if (!product_name && variant_name) {
      return variant_name;
    } else {
      return `No product/variant name`;
    }
  };

  const getSKU = () => {
    if (sku) {
      return `SKU: ${sku as string}`;
    } else {
      return `Product ID: ${product_id}`;
    }
  };

  const getPriceByCurrency = () => {
    if (product_prices) {
      return (
        product_prices.find((item) => item.currency_code === currency)?.value ||
        product_prices[0].value
      );
    }

    return value.toFixed(2);
  };

  return (
    <div>
      <Stack
        onClick={onOpenDetails}
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Badge badgeContent={product_qty as number} color='primary'>
          <CardMedia
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 107,
              height: 80,
              borderRadius: 1,
              backgroundColor: 'grey.300',
            }}
            image={''}
            title={getName()}
          >
            <InsertPhotoOutlinedIcon
              sx={{ width: 24, height: 24, color: 'white' }}
            />
          </CardMedia>
        </Badge>

        <Stack>
          <MUILink
            component={Link}
            underline='none'
            variant='body1'
            to={AppPath.Products + `/${product_id}`}
          >
            {getName()}
          </MUILink>

          <Typography variant='body2' color='text.secondary'>
            {getSKU()}
          </Typography>

          <Typography variant='body2' color='text.secondary'>
            {`Variant ID: ${variant_id?.split('-')[0] || '-'}`}
          </Typography>
        </Stack>

        <Typography
          variant='body2'
          sx={{
            display: 'flex',
            ml: 'auto',
          }}
        >
          {`${product_qty as number} X ${formatCurrency(parseFloat(getPriceByCurrency()), currency)}`}
        </Typography>
      </Stack>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onCloseDetails}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          paper: { style: { width: 300, padding: 16 } },
        }}
      >
        <ProductItemDetailsPopover
          product={product}
          currency={currency}
          onRemoveSelectedProduct={onRemoveSelectedProduct}
          onUpdateSelectedProducts={onUpdateSelectedProducts}
          onCloseDetailsPopover={onCloseDetails}
        />
      </Popover>
    </div>
  );
};

export default ProductItem;
